import PropTypes from "prop-types"
import React, { useMemo, useState, useEffect } from "react"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Input,
  Badge,
  Button,
  UncontrolledTooltip
} from "reactstrap"
import { Link } from "react-router-dom"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { withTranslation } from "react-i18next"
import TableContainer from "../../components/Common/TableContainer"
import CardPagination from "../../components/Common/CardPagination"
import swal from "sweetalert"
import { delhotelreservation, getallhotelreservation, puthotelreservation, notifsend, paidpaid } from "helpers/requests"
import { createPayement } from "helpers/requests"
import PaymentFormModal from "../../components/Common/PaymentFormModal"

// Enum for Hotel Reservation Status
const HotelReservationStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  REFUSED: 'REFUSED',
  CANCELLED: 'CANCELLED',
  HOTEL_ACCEPTED: 'HOTEL_ACCEPTED',
  HOTEL_REFUSED: 'HOTEL_REFUSED'
}

const Hotel = (props) => {
  const [reservations, setReservations] = useState([])
  const [filteredReservations, setFilteredReservations] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedRows, setSelectedRows] = useState([])
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false)

  // Payment modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [paymentSubmitting, setPaymentSubmitting] = useState(false)
  const [paymentTarget, setPaymentTarget] = useState(null)

  // Filters — persisted to localStorage
  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("hf_search") || "")
  const [statusFilter, setStatusFilter] = useState(() => localStorage.getItem("hf_status") || "all")
  const [monthFilter, setMonthFilter] = useState(() => localStorage.getItem("hf_month") || "all")
  const [yearFilter, setYearFilter] = useState(() => localStorage.getItem("hf_year") || "all")
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("hf_sort") || "default")

  // Pagination
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(() => parseInt(localStorage.getItem("hf_pageSize") || "12", 10))

  useEffect(() => {
    localStorage.setItem("hf_search", searchTerm)
    localStorage.setItem("hf_status", statusFilter)
    localStorage.setItem("hf_sort", sortBy)
    localStorage.setItem("hf_month", monthFilter)
    localStorage.setItem("hf_year", yearFilter)
    localStorage.setItem("hf_pageSize", String(pageSize))
  }, [searchTerm, statusFilter, sortBy, monthFilter, yearFilter, pageSize])

  // Reset to first page whenever filters change so users don't land on an empty page
  useEffect(() => {
    setPage(1)
  }, [searchTerm, statusFilter, sortBy, monthFilter, yearFilter, pageSize])

  // Generate years for dropdown (current year and previous 5 years)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return ["all", ...Array.from({ length: 5 }, (_, i) => currentYear - i)];
  }, []);

  // Generate months for dropdown
  const months = useMemo(() => [
    "all", "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ], []);

  useEffect(() => {
    document.title = "Reservation Voyage";

    const fetchReservation = async () => {
      try {
        const response = await getallhotelreservation();
       
        setReservations(response);
        setFilteredReservations(response);
      } catch (error) {
        console.error("Error fetching reservation:", error);
        swal("Erreur", "Impossible de charger les réservations", "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchReservation();
  }, []);

  // Advanced filtering and sorting effect
  useEffect(() => {
    let updatedReservations = [...reservations];

    // Filter by status
    if (statusFilter !== "all") {
      updatedReservations = updatedReservations.filter(
        (res) => res.status === statusFilter
      );
    }

    // Filter by month
    if (monthFilter !== "all") {
      const monthIndex = months.indexOf(monthFilter) - 1;
      updatedReservations = updatedReservations.filter((res) => {
        const resDate = new Date(res.checkinDate);
        return resDate.getMonth() === monthIndex;
      });
    }

    // Filter by year
    if (yearFilter !== "all") {
      updatedReservations = updatedReservations.filter((res) => {
        const resDate = new Date(res.checkinDate);
        return resDate.getFullYear() === parseInt(yearFilter);
      });
    }

    // Search filter
    if (searchTerm) {
      updatedReservations = updatedReservations.filter((res) =>
        `${res.name} ${res.email}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    // Sorting
    if (sortBy === "newest") {
      updatedReservations.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    } else if (sortBy === "oldest") {
      updatedReservations.sort(
        (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
      );
    } else if (sortBy === "highest-price") {
      updatedReservations.sort((a, b) => b.totalPrice - a.totalPrice);
    } else if (sortBy === "lowest-price") {
      updatedReservations.sort((a, b) => a.totalPrice - b.totalPrice);
    }

    setFilteredReservations(updatedReservations);
  }, [reservations, statusFilter, monthFilter, yearFilter, searchTerm, sortBy]);

  const handleSelectAll = () => {
    if (isSelectAllChecked) {
      setSelectedRows([])
    } else {
      setSelectedRows(filteredReservations.map((reservation) => reservation.id))
    }
    setIsSelectAllChecked(!isSelectAllChecked)
  }

  const handleRowSelect = (id) => {
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rowId) => rowId !== id))
    } else {
      setSelectedRows([...selectedRows, id])
    }
  }

  const handlePaid = (reservation) => {
    // Open the modal pre-filled with the reservation's details
    setPaymentTarget(reservation)
    setPaymentModalOpen(true)
  }

  const submitPayment = async (payload) => {
    if (!paymentTarget) return
    setPaymentSubmitting(true)
    try {
      await createPayement(payload)
      await paidpaid(paymentTarget.id)

      // Refresh the reservations list
      const data = await getallhotelreservation();
      setReservations(data);
      setFilteredReservations(data);
      setSelectedRows(data);

      setPaymentModalOpen(false)
      setPaymentTarget(null)
      swal("Réservation payée!", { icon: "success" });
    } catch (error) {
      console.error("Error processing payment:", error);
      swal("Erreur", "Impossible de traiter le paiement", "error");
    } finally {
      setPaymentSubmitting(false)
    }
  }

  const handleSendToHotel = async (reservation) => {
    swal({
      title: "Êtes-vous sûr?",
      text: "Voulez-vous vraiment envoyer cette réservation à l'hôtel?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willSend) => {
      if (willSend) {
        try {
          console.log(reservation.id)
          await notifsend(reservation.id);
          await puthotelreservation(reservation.id, { ...reservation,ispayed: false });
          
          // Refresh the reservations list
          const data = await getallhotelreservation();
          setReservations(data);
          setFilteredReservations(data);
          
          swal("Réservation envoyée à l'hôtel!", { icon: "success" });
        } catch (error) {
          console.error("Error sending reservation to hotel:", error);
          swal("Erreur", "Impossible d'envoyer la réservation à l'hôtel", "error");
        }
      } else {
        swal("Envoi annulé");
      }
    });
  };

  const handleDelete = (reservationData) => {
    swal({
      title: "Êtes-vous sûr?",
      text: "Une fois supprimé, vous ne pourrez pas récupérer cette réservation!",
      icon: "warning",
      buttons: ["Annuler", "Supprimer"],
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        await delhotelreservation(reservationData.id);

        const data = await getallhotelreservation();
        setReservations(data);
        setFilteredReservations(data);
        setSelectedRows(data);
        swal("La réservation a été supprimée!", {
          icon: "success",
        });
      } else {
        swal("Suppression annulée");
      }
    });
  };

  const handleCheck = (reservationData) => {
    // Navigate to checkout or perform check-in action
    window.location.href = `/hotel-checkout/${reservationData.id}`;
  };

  // Function to render status badge
  const renderStatusBadge = (status) => {
    const badgeColors = {
      [HotelReservationStatus.PENDING]: 'warning',
      [HotelReservationStatus.ACCEPTED]: 'success',
      [HotelReservationStatus.REFUSED]: 'danger',
      [HotelReservationStatus.CANCELLED]: 'secondary',
      [HotelReservationStatus.HOTEL_ACCEPTED]: 'success',
      [HotelReservationStatus.HOTEL_REFUSED]: 'danger',
    };
    return (
      <Badge color={badgeColors[status] || 'secondary'} className="font-size-10">
        {status}
      </Badge>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: () => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                id="checkAll"
                checked={isSelectAllChecked}
                onChange={handleSelectAll}
              />
            </Label>
          </FormGroup>
        ),
        accessorKey: "id",
        cell: (cellProps) => (
          <FormGroup check className="font-size-16">
            <Label check>
              <Input
                type="checkbox"
                checked={selectedRows.includes(cellProps.row.original.id)}
                onChange={() => handleRowSelect(cellProps.row.original.id)}
              />
            </Label>
          </FormGroup>
        ),
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Nom",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Check-in",
        accessorKey: "checkinDate",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => new Date(cellProps.row.original.checkinDate).toLocaleDateString(),
      },
      {
        header: "Check-out",
        accessorKey: "checkoutDate",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => new Date(cellProps.row.original.checkoutDate).toLocaleDateString(),
      },
      {
        header: "Montant Total",
        accessorKey: "totalPrice",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => `${cellProps.row.original.totalPrice} DA`,
      },
      {
        header: "Statut",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => renderStatusBadge(cellProps.row.original.status),
      },
      {
        header: "Payement",
        accessorKey: "ispayed",
        enableColumnFilter: false,
        enableSorting: true,
        cell: (cellProps) => (
          <Badge
            color={
              cellProps.row.original.ispayed === true
                ? "success"  // Green for paid
                : "warning"  // Yellow/warning for unpaid
            }
            className="font-size-10"
          >
            {cellProps.row.original.ispayed ? "Paid" : "Not Paid"}
          </Badge>
        ),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cellProps) => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              className="text-primary"
              id={`checktooltip-${cellProps.row.original.id}`}
              onClick={() => handleCheck(cellProps.row.original)}
            >
              <i className="mdi mdi-eye font-size-18" />
              <UncontrolledTooltip placement="top" target={`checktooltip-${cellProps.row.original.id}`}>
                Detail
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => handleDelete(cellProps.row.original)}
            >
              <i className="mdi mdi-delete font-size-18" id={`deletetooltip-${cellProps.row.original.id}`} />
              <UncontrolledTooltip placement="top" target={`deletetooltip-${cellProps.row.original.id}`}>
                Delete
              </UncontrolledTooltip>
            </Link>
            {!cellProps.row.original.sendnotif && (
              <Link
                to="#"
                className="text-primary"
                id={`sendtotooltip-${cellProps.row.original.id}`}
                onClick={() => handleSendToHotel(cellProps.row.original)}
              >
                <i className="fas fa-hotel font-size-18" />
                <UncontrolledTooltip
                  placement="top"
                  target={`sendtotooltip-${cellProps.row.original.id}`}
                >
                  Send reservation to hotel
                </UncontrolledTooltip>
              </Link>
            )}
            {!cellProps.row.original.ispayed && (
              <Link
                to="#"
                className="text-primary"
                id={`paidtooltipa-${cellProps.row.original.id}`}
                onClick={() => handlePaid(cellProps.row.original)}
              >
                <i className="mdi mdi-currency-usd font-size-18" />
                <UncontrolledTooltip
                  placement="top"
                  target={`paidtooltipa-${cellProps.row.original.id}`}
                >
                  Check payment
                </UncontrolledTooltip>
              </Link>
            )}
          </div>
        ),
      },
    ],
    [selectedRows, isSelectAllChecked]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Réservations")} breadcrumbItem={props.t("Réservations d'hôtel")} />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle style={{ marginBottom: "40px" }}>
                    {props.t("Réservations d'hôtel")}
                  </CardTitle>

                  {/* Filters */}
                  <Row className="mb-3">
                    <Col md="3">
                      <FormGroup>
                        <Label for="statusFilter">Filtrer par Statut</Label>
                        <Input
                          type="select"
                          id="statusFilter"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="all">Tous</option>
                          <option value="PENDING">En attente</option>
                          <option value="ACCEPTED">Accepté</option>
                          <option value="REFUSED">Refusé</option>
                          <option value="HOTEL_ACCEPTED">Accepté par l'hôtel</option>
                          <option value="HOTEL_REFUSED">Refusé par l'hôtel</option>
                          <option value="CANCELLED">Annulé</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="monthFilter">Filtrer par Mois</Label>
                        <Input
                          type="select"
                          id="monthFilter"
                          value={monthFilter}
                          onChange={(e) => setMonthFilter(e.target.value)}
                        >
                          {months.map((month) => (
                            <option key={month} value={month}>
                              {month}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="yearFilter">Filtrer par Année</Label>
                        <Input
                          type="select"
                          id="yearFilter"
                          value={yearFilter}
                          onChange={(e) => setYearFilter(e.target.value)}
                        >
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="sortBy">Trier par</Label>
                        <Input
                          type="select"
                          id="sortBy"
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value)}
                        >
                          <option value="default">Défaut</option>
                          <option value="newest">Plus récent</option>
                          <option value="oldest">Plus ancien</option>
                          <option value="highest-price">Prix le plus élevé</option>
                          <option value="lowest-price">Prix le plus bas</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Search Input */}
                  <Row className="mb-3">
                    <Col md="12">
                      <FormGroup>
                        <Label for="searchInput">Recherche</Label>
                        <Input
                          type="text"
                          id="searchInput"
                          placeholder="Rechercher par nom ou email"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {isLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status" />
                      <p className="text-muted mt-3 mb-0">Chargement des réservations…</p>
                    </div>
                  ) : filteredReservations.length === 0 ? (
                    <div className="text-center text-muted py-5 border border-light rounded-3">
                      <i className="mdi mdi-bed-empty display-4 d-block mb-2" />
                      <h5 className="mt-3">Aucune réservation</h5>
                      <p className="mb-0">Aucun résultat ne correspond aux filtres.</p>
                    </div>
                  ) : (
                    <Row>
                      {filteredReservations
                        .slice((page - 1) * pageSize, page * pageSize)
                        .map((res) => {
                          const statusColor =
                            {
                              PENDING: "warning",
                              ACCEPTED: "success",
                              REFUSED: "danger",
                              CANCELLED: "secondary",
                              HOTEL_ACCEPTED: "success",
                              HOTEL_REFUSED: "danger",
                            }[res.status] || "secondary";
                          const nights = res.totalNights ||
                            Math.max(0, Math.round(
                              (new Date(res.checkoutDate) - new Date(res.checkinDate)) /
                              (1000 * 60 * 60 * 24)
                            ));
                          return (
                            <Col xl={4} lg={6} md={6} key={res.id}>
                              {/* ── STAY CARD — soft sky header + date-timeline body ── */}
                              <Card
                                className="border h-100 overflow-hidden"
                                style={{
                                  borderColor: "#d0e0f5 !important",
                                  boxShadow: "0 2px 10px rgba(0, 0, 0, 0.04)",
                                }}
                              >
                                {/* Top header — soft sky band with bed icon */}
                                <div
                                  style={{
                                    background:
                                      "linear-gradient(135deg, #eaf3ff 0%, #d8ebff 100%)",
                                    padding: "12px 18px",
                                    borderBottom: "1px solid #d0e0f5",
                                  }}
                                >
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="me-3"
                                      style={{
                                        width: 40, height: 40,
                                        borderRadius: 10,
                                        background: "#fff",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        boxShadow: "0 1px 3px rgba(4, 50, 99, 0.08)",
                                      }}
                                    >
                                      <i
                                        className="bx bx-bed"
                                        style={{ fontSize: 22, color: "#0a4d8f" }}
                                      />
                                    </div>
                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                      <small
                                        className="text-uppercase d-block fw-bold"
                                        style={{ color: "#0a4d8f", fontSize: 10, letterSpacing: 1 }}
                                      >
                                        Hôte
                                      </small>
                                      <h6
                                        className="mb-0 text-truncate"
                                        style={{ color: "#043263" }}
                                      >
                                        {res.name} {res.surnom}
                                      </h6>
                                      <small
                                        className="text-truncate d-block"
                                        style={{ color: "#5a7a9e", fontSize: 11 }}
                                      >
                                        {res.email || "—"}
                                      </small>
                                    </div>
                                    <Badge color={statusColor} className="font-size-10">
                                      {res.status}
                                    </Badge>
                                  </div>
                                </div>

                                <CardBody>
                                  {/* Date timeline */}
                                  <div className="d-flex align-items-center mb-3">
                                    <div className="text-center" style={{ width: "35%" }}>
                                      <small className="text-muted text-uppercase d-block font-size-10">
                                        Arrivée
                                      </small>
                                      <strong className="d-block font-size-15 text-primary">
                                        {new Date(res.checkinDate).getDate()}
                                      </strong>
                                      <small className="text-muted">
                                        {new Date(res.checkinDate).toLocaleDateString("fr-FR", {
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </small>
                                    </div>

                                    <div className="position-relative flex-grow-1 mx-2" style={{ height: 2, background: "#e5e9f2" }}>
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: -10,
                                          left: 0,
                                          right: 0,
                                          textAlign: "center",
                                        }}
                                      >
                                        <Badge color="primary" className="font-size-10">
                                          <i className="mdi mdi-weather-night me-1" />
                                          {nights} {nights > 1 ? "nuits" : "nuit"}
                                        </Badge>
                                      </div>
                                    </div>

                                    <div className="text-center" style={{ width: "35%" }}>
                                      <small className="text-muted text-uppercase d-block font-size-10">
                                        Départ
                                      </small>
                                      <strong className="d-block font-size-15 text-primary">
                                        {new Date(res.checkoutDate).getDate()}
                                      </strong>
                                      <small className="text-muted">
                                        {new Date(res.checkoutDate).toLocaleDateString("fr-FR", {
                                          month: "short",
                                          year: "numeric",
                                        })}
                                      </small>
                                    </div>
                                  </div>

                                  {/* Total + payment */}
                                  <div
                                    className="d-flex justify-content-between align-items-center px-3 py-2 rounded-3"
                                    style={{ background: "#f7faff" }}
                                  >
                                    <div>
                                      <small className="text-muted text-uppercase d-block font-size-10">
                                        Montant
                                      </small>
                                      <strong className="text-primary font-size-15">
                                        {Number(res.totalPrice || 0).toLocaleString("fr-DZ")} DA
                                      </strong>
                                    </div>
                                    <Badge
                                      color={res.ispayed ? "success" : "warning"}
                                      className="font-size-10"
                                    >
                                      <i className={`mdi ${res.ispayed ? "mdi-check-circle" : "mdi-clock-outline"} me-1`} />
                                      {res.ispayed ? "Payé" : "Non payé"}
                                    </Badge>
                                  </div>

                                  {/* Footer actions */}
                                  <div className="border-top mt-3 pt-3 d-flex justify-content-end gap-3">
                                    <Link
                                      to="#"
                                      className="text-primary"
                                      id={`cardview-${res.id}`}
                                      onClick={() => handleCheck(res)}
                                    >
                                      <i className="mdi mdi-eye font-size-18" />
                                      <UncontrolledTooltip placement="top" target={`cardview-${res.id}`}>
                                        Détails
                                      </UncontrolledTooltip>
                                    </Link>
                                    <Link
                                      to="#"
                                      className="text-danger"
                                      id={`carddel-${res.id}`}
                                      onClick={() => handleDelete(res)}
                                    >
                                      <i className="mdi mdi-delete font-size-18" />
                                      <UncontrolledTooltip placement="top" target={`carddel-${res.id}`}>
                                        Supprimer
                                      </UncontrolledTooltip>
                                    </Link>
                                    {!res.sendnotif && (
                                      <Link
                                        to="#"
                                        className="text-primary"
                                        id={`cardsend-${res.id}`}
                                        onClick={() => handleSendToHotel(res)}
                                      >
                                        <i className="fas fa-hotel font-size-18" />
                                        <UncontrolledTooltip placement="top" target={`cardsend-${res.id}`}>
                                          Envoyer à l'hôtel
                                        </UncontrolledTooltip>
                                      </Link>
                                    )}
                                    {!res.ispayed && (
                                      <Link
                                        to="#"
                                        className="text-primary"
                                        id={`cardpay-${res.id}`}
                                        onClick={() => handlePaid(res)}
                                      >
                                        <i className="mdi mdi-currency-usd font-size-18" />
                                        <UncontrolledTooltip placement="top" target={`cardpay-${res.id}`}>
                                          Enregistrer le paiement
                                        </UncontrolledTooltip>
                                      </Link>
                                    )}
                                  </div>
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                    </Row>
                  )}
                  {!isLoading && filteredReservations.length > 0 && (
                    <CardPagination
                      page={page}
                      pageSize={pageSize}
                      total={filteredReservations.length}
                      onPageChange={setPage}
                      onPageSizeChange={setPageSize}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <PaymentFormModal
          isOpen={paymentModalOpen}
          toggle={() => setPaymentModalOpen(false)}
          type="HOTEL_RESERVATION"
          linkId={paymentTarget?.id}
          sourceLabel={
            paymentTarget
              ? `Réservation ${paymentTarget.name || ""} ${paymentTarget.surnom || ""}`.trim()
              : ""
          }
          prefill={{
            firstname: paymentTarget?.name || "",
            lastname: paymentTarget?.surnom || "",
            phonenumber: paymentTarget?.phone || "",
            email: paymentTarget?.email || "",
            amount: paymentTarget?.totalPrice || "",
          }}
          submitting={paymentSubmitting}
          onSubmit={submitPayment}
        />
      </div>
    </React.Fragment>
  );
};

Hotel.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(Hotel);